import { ProjectListItem } from 'presentations/api/schema/project';
import { TeamUserListItem } from 'presentations/api/schema/team_user';
import { ViewerItem } from 'presentations/api/schema/user';
import { SyntheticEvent, useEffect, useState } from 'react';
import {
  getPersistentItemWithViewer,
  removePersistentItemWithViewer,
} from 'presentations/views/helpers/storage';
import {
  selectedUserIdsKey,
  selectedProjectIdsKey,
} from '../HomeVisibleSettingForm/hooks';
import { useRouter } from 'next/router';
import { adminUserListPath, apiPath, webPath } from 'presentations/routes/path';
import { get, patch } from 'infras/rest_client';
import { TeamUserOrderSyncBody } from 'presentations/api/schema/team_user_order';
import { SortType } from 'domains/models/team_user_order/sortType';
import { SortType as ProjectSortType } from 'domains/models/project_order/sortType';
import { useRequest } from 'presentations/views/hooks/request';
import { DropResult } from 'react-beautiful-dnd';
import { ProjectOrderSyncBody } from 'presentations/api/schema/project_order';
import { Status } from 'domains/models/user_migration/status';
import { OrderSettingMigrationStatusItem } from 'presentations/api/schema/user_migration';

type Props = {
  viewer: ViewerItem;
  teamUsers: TeamUserListItem[]; // localStorageからのmigration用にしばらくは必要
  projects: ProjectListItem[];
  openProjectForm: () => void;
  // openInviteMemberDialog: () => void;
};

export const useHomeDisplayItemSettingForm = (props: Props) => {
  const [isOpen, setOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [
    teamUserOrderMigrationStatus,
    setTeamUserOrderMigrationStatus,
  ] = useState<Status | null>(null);
  const [
    projectOrderMigrationStatus,
    setProjectOrderMigrationStatus,
  ] = useState<Status | null>(null);

  const viewerUid = props.viewer.uid;

  const isInviteMember = selectedTab === 0;
  const isAddProject = selectedTab === 1;

  const addButtonText = isInviteMember ? 'Invite member' : 'Add project';
  const autoCompleteName = isInviteMember ? 'member' : 'project';
  const autoCompletePlaceholder = isInviteMember
    ? 'Placeholder Member'
    : 'Placeholder Project';
  const allAddedText = null;

  const selectedTeamUsersRequest = useRequest(
    apiPath(`/v1/teams/${props.viewer.team!.uid}/users`, {
      page: 1,
      perPage: 1000,
      sortType: SortType.Top,
    }),
  );
  const selectedUsers: TeamUserListItem[] = selectedTeamUsersRequest.data ?? [];

  const selectedUserProjectsRequest = useRequest(
    apiPath(
      `/v1/teams/${props.viewer.team!.uid}/users/${props.viewer.uid}/projects`,
      {
        page: 1,
        perPage: 1000,
        sortType: ProjectSortType.Top,
      },
    ),
  );
  const selectedProjects: ProjectListItem[] =
    selectedUserProjectsRequest.data ?? [];

  // localStorageからのmigration用にしばらくは必要
  useEffect(() => {
    (async () => {
      if (!teamUserOrderMigrationStatus || !projectOrderMigrationStatus) {
        const result = await get(
          apiPath(
            `/v1/teams/${
              props.viewer.team!.uid
            }/users/order_setting_migration_status`,
          ),
        );
        if (!result.isSuccess) {
          return;
        }
        const data: OrderSettingMigrationStatusItem = result.data;
        setTeamUserOrderMigrationStatus(data.teamUserOrderStatus);
        setProjectOrderMigrationStatus(data.projectOrderStatus);
      }
    })();
  }, [teamUserOrderMigrationStatus, projectOrderMigrationStatus]);

  useEffect(() => {
    if (props.teamUsers.length > 0) {
      (async () => {
        if (teamUserOrderMigrationStatus === null) {
          return;
        }

        // 1. migration済みか確認
        if (teamUserOrderMigrationStatus === Status.NotStarted) {
          // 2.最初にlocalStorageから取得して100件から取得しているデータを表示する
          const selectedUserIds = getPersistentItemWithViewer(
            selectedUserIdsKey,
            props.viewer,
          );

          let migrateUsers: TeamUserListItem[] = [];
          if (Array.isArray(selectedUserIds) && selectedUserIds.length > 0) {
            // localStorageにデータがあるケースは移行する
            migrateUsers = props.teamUsers.filter((e) =>
              selectedUserIds.includes(e.id),
            );
          } else {
            // localStorageにデータがないケースはdefault100取得データを移行する
            migrateUsers = props.teamUsers;
          }

          // 3.requestに反映
          selectedTeamUsersRequest.mutate(
            {
              ...selectedTeamUsersRequest,
              data: migrateUsers,
            },
            false,
          );

          // 4.DBに同期する(migration済みフラグも立てる)
          const result = await syncTeamUser(migrateUsers, true);

          // 5. state更新
          setTeamUserOrderMigrationStatus(Status.Completed);
        }
      })();
    }
  }, [props.teamUsers, teamUserOrderMigrationStatus]);

  // localStorageからのmigration用にしばらくは必要
  useEffect(() => {
    if (props.projects.length > 0) {
      (async () => {
        if (projectOrderMigrationStatus === null) {
          return;
        }

        // 1. migration済みか確認
        if (projectOrderMigrationStatus === Status.NotStarted) {
          // 2.最初にlocalStorageから取得して100件から取得しているデータを表示する
          const selectedProjectIds = getPersistentItemWithViewer(
            selectedProjectIdsKey,
            props.viewer,
          );

          let migrateProjects: ProjectListItem[] = [];
          if (
            Array.isArray(selectedProjectIds) &&
            selectedProjectIds.length > 0
          ) {
            migrateProjects = props.projects.filter((e) =>
              selectedProjectIds.includes(e.id),
            );
          } else {
            migrateProjects = props.projects;
          }

          // 3.requestに反映
          selectedUserProjectsRequest.mutate(
            {
              ...selectedUserProjectsRequest,
              data: migrateProjects,
            },
            false,
          );

          // 4.DBに同期する
          const result = await syncProject(migrateProjects, true);

          // 5. state更新
          setProjectOrderMigrationStatus(Status.Completed);
        }
      })();
    }
  }, [props.projects, projectOrderMigrationStatus]);

  const syncTeamUser = async (
    newTeamUsers: TeamUserListItem[],
    isMigrated?: boolean,
  ) => {
    try {
      const users = newTeamUsers.map((u, index) => {
        return {
          userId: u.userId,
          sort: index,
        };
      });
      const data: TeamUserOrderSyncBody = {
        sortType: SortType.Top,
        users,
      };
      if (isMigrated) {
        data.isUpdateMigrationStatus = true;
      }
      const result = await patch(
        apiPath(`/v1/teams/${props.viewer.team?.uid}/team_user_orders/sync`),
        {
          data,
        },
      );

      selectedTeamUsersRequest.mutate();

      return result;
    } catch (error) {
      // 表示上の並び順を戻す
      selectedTeamUsersRequest.mutate(
        {
          ...selectedTeamUsersRequest,
          data: selectedUsers,
        },
        false,
      );
    }
  };

  const onProjectAdded = async (newProject: ProjectListItem) => {
    selectedUserProjectsRequest.mutate(
      {
        ...selectedUserProjectsRequest,
        data: [...selectedProjects, newProject],
      },
      false,
    );
  };

  const onProjectDeleted = async (project: ProjectListItem) => {
    selectedUserProjectsRequest.mutate(
      {
        ...selectedUserProjectsRequest,
        data: selectedProjects.filter((e) => e.id !== project.id),
      },
      false,
    );
  };

  const syncProject = async (
    newProjects: ProjectListItem[],
    isMigrated?: boolean,
  ) => {
    try {
      const projects = newProjects.map((p, index) => {
        return {
          projectId: p.id!,
          sort: index,
        };
      });
      const data: ProjectOrderSyncBody = {
        sortType: ProjectSortType.Top,
        projects,
      };
      if (isMigrated) {
        data.isUpdateMigrationStatus = true;
      }
      const result = await patch(
        apiPath(`/v1/teams/${props.viewer.team?.uid}/project_orders/sync`),
        {
          data,
        },
      );

      selectedUserProjectsRequest.mutate();

      return result;
    } catch (error) {
      // 表示上の並び順を戻す
      selectedUserProjectsRequest.mutate(
        {
          ...selectedUserProjectsRequest,
          data: selectedProjects,
        },
        false,
      );
    }
  };

  const onChangeTab = (e: SyntheticEvent, value: number) => {
    setSelectedTab(value);
  };

  // // オプションを選択した時に呼ばれるcallback
  const onSelectNewProject = (
    e: SyntheticEvent,
    value: { uid: string } | null,
  ) => {
    if (value) {
      const project = props.projects.find((p) => p.uid === value.uid);
      if (project) {
        selectedUserProjectsRequest.mutate(
          {
            ...selectedUserProjectsRequest,
            data: [...selectedProjects, project],
          },
          false,
        );
      }
    }
  };

  const onSelectNewUser = (newUser: TeamUserListItem) => {
    selectedTeamUsersRequest.mutate(
      {
        ...selectedTeamUsersRequest,
        data: [...selectedUsers, newUser],
      },
      false,
    );
  };

  const onDeleteUser = (uid: string) => {
    const users = selectedUsers.filter((u) => u.userId !== uid);
    selectedTeamUsersRequest.mutate(
      {
        ...selectedTeamUsersRequest,
        data: users,
      },
      false,
    );
    syncTeamUser(users);
  };

  const onUserDragEnd = (result: DropResult) => {
    if (!result.destination || !selectedUsers || selectedUsers.length <= 1) {
      return;
    }

    // 動かした要素を移動先へ移動する（先頭は変わらないので固定するために1 + indexする）
    const newSelectedUsers = [...selectedUsers];
    const [moved] = newSelectedUsers.splice(1 + result.source.index, 1);
    newSelectedUsers.splice(1 + result.destination.index, 0, moved);
    selectedTeamUsersRequest.mutate(
      {
        ...selectedTeamUsersRequest,
        data: newSelectedUsers,
      },
      false,
    );
    syncTeamUser(newSelectedUsers);
  };

  const onProjectDragEnd = (result: DropResult) => {
    if (!result.destination || !selectedProjects) {
      return;
    }
    const newSelectedProjects = [...selectedProjects];
    const [moved] = newSelectedProjects.splice(result.source.index, 1);
    newSelectedProjects.splice(result.destination.index, 0, moved);
    newSelectedProjects.forEach((project, idx) => {
      project.sort = idx;
    });
    newSelectedProjects.sort((a, b) => (a.sort < b.sort ? -1 : 1));
    selectedUserProjectsRequest.mutate(
      {
        ...selectedUserProjectsRequest,
        data: newSelectedProjects,
      },
      false,
    );
    syncProject(newSelectedProjects);
  };

  const onDeleteProject = (uid: string) => {
    const projects = selectedProjects.filter((p) => p.uid !== uid);
    selectedUserProjectsRequest.mutate(
      {
        ...selectedUserProjectsRequest,
        data: projects,
      },
      false,
    );
    syncProject(projects);
  };

  const setOpenInviteMember = () => {
    setSelectedTab(0);
    setOpen(true);
  };

  const setOpenAddProject = () => {
    setSelectedTab(1);
    setOpen(true);
  };

  const onSubmit = async () => {
    if (isInviteMember) {
      const data: TeamUserOrderSyncBody = {
        sortType: SortType.Top,
        users: [],
      };
      selectedUsers.forEach((u, index) => {
        data.users.push({
          userId: u.userId,
          sort: index,
        });
      });

      try {
        await patch(
          apiPath(`/v1/teams/${props.viewer.team?.uid}/team_user_orders/sync`),
          {
            data,
          },
        );
      } catch (error) {
        console.log(error);
      }
    } else if (isAddProject) {
      const data: ProjectOrderSyncBody = {
        sortType: ProjectSortType.Top,
        projects: [],
      };
      selectedProjects.forEach((p, index) => {
        if (p.id) {
          data.projects.push({
            projectId: p.id,
            sort: index,
          });
        }
      });

      try {
        await patch(
          apiPath(`/v1/teams/${props.viewer.team?.uid}/project_orders/sync`),
          {
            data,
          },
        );
      } catch (error) {
        console.log(error);
      }
    }
    setOpen(false);
  };

  const onClose = () => {
    setOpen(false);
  };

  const router = useRouter();
  const onClickAddButton = () => {
    if (isInviteMember) {
      // props.openInviteMemberDialog();
      window.open(webPath(adminUserListPath(), router.locale));
    } else if (isAddProject) {
      props.openProjectForm();
    }
  };

  const canSubmit = isInviteMember
    ? selectedUsers.length > 0
    : isAddProject
    ? selectedProjects.length > 0
    : false;

  return {
    state: {
      viewer: props.viewer,
      viewerUid,
      isLoading: false,
      isOpen,
      isInviteMember,
      isAddProject,
      selectedTab,
      addButtonText,
      allAddedText,
      autoCompleteName,
      autoCompletePlaceholder,
      selectedUsers,
      selectedProjects,
      canSubmit,
      projects: props.projects,
    },
    actions: {
      onChangeTab,
      onDeleteUser,
      onDeleteProject,
      setOpenInviteMember,
      setOpenAddProject,
      onSubmit,
      onClose,
      onClickAddButton,
      onUserDragEnd,
      onProjectDragEnd,
      onSelectNewUser,
      onSelectNewProject,
      onProjectAdded,
      onProjectDeleted,
      syncTeamUser,
      syncProject,
    },
  };
};
